import React, {useCallback} from 'react'
import {useSelector} from 'react-redux'
import styled from '@emotion/styled'
import {getAnonymousId} from 'modules/meta/selectors'

import {Box} from '@vio/atlas/helpers/Box'
import {Icon} from '@vio/atlas/Icon'
import {Image} from '@vio/atlas/Image'
import {Tag} from '@vio/atlas/Tag'
import {Text} from '@vio/atlas/Text'
import {cssTheme} from '@vio/atlas/themes'
import {linariaMq} from '@vio/atlas/utils/breakpoints'
import {Entity, Page} from '@vio/core/src/analytics/types/Events'
import {AuthenticationFlows} from '@vio/core/src/auth/types/Auth'
import {getVioBrand} from '@vio/core/src/brand/utils'
import FormattedMessageWrapper from '@vio/core/src/localization/components/FormattedMessage'
import {buildUrl} from '@vio/core/src/utils/url'
import {useLogIn} from '@vio/shared/src/authentication/hooks/useLogIn'

import {getSearchResultsUrl} from '../../utils/bookingManagement/urls'
import {useHopperAdTracking} from './hooks/useHopperAdTracking'
import {HopperAd} from './types'

const CARD_WIDTH = 192
const CARD_WIDTH_MOBILE = 155
const IMAGE_HEIGHT = 99
const IMAGE_HEIGHT_MOBILE = 78
const CardWrapper = styled.div`
  border-radius: ${cssTheme.layout.radius.lg};
  background-color: ${cssTheme.colors.background.brand.tertiary};
  overflow: hidden;
  padding: ${cssTheme.layout.spacing.s300};
  min-width: ${CARD_WIDTH_MOBILE}px;
  max-width: ${CARD_WIDTH_MOBILE}px;
  display: flex;
  flex-direction: column;
  row-gap: ${cssTheme.layout.spacing.s300};
  cursor: pointer;
  ${linariaMq.desktopXs} {
    min-width: ${CARD_WIDTH}px;
    max-width: ${CARD_WIDTH}px;
  }
`

const ImageContainer = styled.div`
  height: ${IMAGE_HEIGHT_MOBILE}px;
  width: 100%;
  flex-shrink: 0;
  position: relative;
  ${linariaMq.desktopXs} {
    height: ${IMAGE_HEIGHT}px;
  }
`

const StyledTag = styled(Tag)`
  width: fit-content;
  position: absolute;
  top: ${cssTheme.layout.spacing.s250};
  left: ${cssTheme.layout.spacing.s250};
  margin-right: ${cssTheme.layout.spacing.s250};
  ${linariaMq.desktopXs} {
    top: ${cssTheme.layout.spacing.s300};
    left: ${cssTheme.layout.spacing.s300};
    margin-right: ${cssTheme.layout.spacing.s300};
  }
`

const StyledTitle = styled(Text)`
  max-width: 100%;
  display: -webkit-box;
  overflow: hidden;
  line-clamp: 1;
  -webkit-line-clamp: 1;
  -webkit-box-orient: vertical;
`

interface Props {
  ad: HopperAd
}
export const HopperCarouselItem = React.memo(({ad}: Props) => {
  const {impressionRef, trackClick} = useHopperAdTracking({
    ad,
    entity: Entity.HopperCarousel,
    page: Page.Home
  })
  const anonymousId = useSelector(getAnonymousId)
  const logIn = useLogIn()

  const {creative, targetProduct} = ad

  const handleOnClick = useCallback(() => {
    trackClick()

    const url = targetProduct?.productIdentifierValue
      ? buildUrl(getSearchResultsUrl(getVioBrand()), {
          placeId: targetProduct.productIdentifierValue
        })
      : undefined

    logIn({
      flow: AuthenticationFlows.SignUp,
      sourceComponent: 'HopperCarousel',
      anonymousId,
      onAuthSuccessLandingUrl: url,
      analyticsData: {
        component: 'HopperCarousel',
        adId: ad.adId,
        placeId: targetProduct.productIdentifierValue
      }
    })
  }, [trackClick, logIn, anonymousId, ad, targetProduct])

  return (
    <CardWrapper onClick={handleOnClick} ref={impressionRef}>
      <ImageContainer>
        <Image
          fetchPriority="auto"
          alt={creative.shortCampaignText}
          url={creative.imageUrl}
          borderRadius="md"
        />
        {creative.badgeText ? (
          <StyledTag variant="neutralOutline" size="sm">
            {creative.badgeText}
          </StyledTag>
        ) : null}
      </ImageContainer>
      <Box
        paddingRight="s200"
        paddingLeft="s200"
        paddingBottom="s200"
        justifyContent="space-between"
        display="flex"
        flexDirection="column"
      >
        <Box marginBottom="s200">
          <StyledTitle variant="titleM">
            {creative.shortCampaignText || creative.destinationName}
          </StyledTitle>
        </Box>
        <Box
          display="flex"
          flexDirection="row"
          color="content.brand.primary"
          alignItems="center"
        >
          <Text variant="titleXS">
            {creative.ctaText || (
              <FormattedMessageWrapper
                id="hopper.claimNow"
                defaultMessage="Claim Now"
              />
            )}
          </Text>
          <Icon size="sm" name="ChevronRight" />
        </Box>
      </Box>
    </CardWrapper>
  )
})

HopperCarouselItem.displayName = 'HopperCarouselItem'
